import React, { useState, useRef } from 'react';
import { Formik, Form, Field, ErrorMessage, FieldArray } from 'formik';
import * as Yup from 'yup';
import { XMarkIcon, PlusIcon, TrashIcon, DocumentIcon } from '@heroicons/react/24/outline';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { usePublications } from '../../contexts/PublicationContext';
import { Publication, createPublication, updatePublication, deletePublication, updatePublicationOrder } from '../../firebase/publicationService';
import { toast } from 'react-hot-toast';

// Validation schema for publication form
const publicationSchema = Yup.object().shape({
  title: Yup.string().required('Title is required'),
  authors: Yup.array().of(Yup.string()).min(1, 'At least one author is required'),
  year: Yup.string().required('Year is required'),
  conference: Yup.string(),
  journal: Yup.string(),
  location: Yup.string(),
  doi: Yup.string(),
  abstract: Yup.string(),
  keywords: Yup.array().of(Yup.string()),
  link: Yup.string().url('Must be a valid URL'),
});

// Initial values for publication form
const initialValues: Omit<Publication, 'id' | 'createdAt' | 'updatedAt'> = {
  title: '',
  authors: [''],
  year: '',
  conference: '',
  journal: '',
  location: '',
  doi: '',
  abstract: '',
  keywords: [''],
  link: '',
  pdfUrl: '',
  coverImage: '',
  featured: false,
  order: 0,
};

const PublicationsEditor: React.FC = () => {
  const { publications, loading, refreshPublications } = usePublications();
  const [selectedPublication, setSelectedPublication] = useState<Publication | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [coverPreview, setCoverPreview] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle cover image file selection
  const handleCoverChange = (e: React.ChangeEvent<HTMLInputElement>, setFieldValue: any) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      
      reader.onloadend = () => {
        const base64 = reader.result as string;
        setCoverPreview(base64);
        setFieldValue('coverImage', base64);
      };
      
      reader.readAsDataURL(file);
    }
  };

  // Reset form when adding new publication
  const handleAddNew = () => {
    setSelectedPublication(null);
    setIsAdding(true);
    setCoverPreview('');
  };

  // Edit existing publication
  const handleEdit = (pub: Publication) => {
    setSelectedPublication(pub);
    setIsAdding(false);
    setCoverPreview(pub.coverImage || '');
  };

  // Cancel editing or adding
  const handleCancel = () => {
    setSelectedPublication(null);
    setIsAdding(false);
    setCoverPreview('');
  };

  // Delete publication entry
  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this publication?')) {
      try {
        await deletePublication(id);
        toast.success('Publication deleted successfully');
        refreshPublications();
      } catch (error) {
        console.error('Error deleting publication:', error);
        toast.error('Failed to delete publication');
      }
    }
  };

  // Handle form submission
  const handleSubmit = async (values: any, { resetForm }: any) => {
    try {
      const publicationData = { ...values };
      
      // Filter out empty authors and keywords
      publicationData.authors = publicationData.authors.filter((author: string) => author.trim() !== '');
      publicationData.keywords = publicationData.keywords.filter((keyword: string) => keyword.trim() !== '');
      
      if (selectedPublication && selectedPublication.id) {
        await updatePublication(selectedPublication.id, publicationData);
        toast.success('Publication updated successfully');
      } else {
        await createPublication(publicationData);
        toast.success('Publication added successfully');
      }
      
      resetForm();
      setSelectedPublication(null);
      setIsAdding(false);
      setCoverPreview('');
      refreshPublications();
    } catch (error) {
      console.error('Error saving publication:', error);
      toast.error('Failed to save publication');
    }
  };

  // Handle drag and drop reordering
  const handleDragEnd = async (result: any) => {
    if (!result.destination) return;
    
    const items = Array.from(publications);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    // Update the order property of each publication item
    const reorderedPublications = items.map((item, index) => ({
      id: item.id!,
      order: index
    }));
    
    try {
      await updatePublicationOrder(reorderedPublications);
      refreshPublications();
    } catch (error) {
      console.error('Error updating order:', error);
      toast.error('Failed to update order');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="pb-12">
      <h1 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Publications Editor</h1>
      <p className="text-gray-600 dark:text-gray-300 mb-4">
        Manage your research publications and papers here. Drag and drop to reorder.
      </p>
      
      {/* Publications List */}
      {publications.length > 0 && !isAdding && !selectedPublication && (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Publication Entries</h2>
            <button
              type="button"
              onClick={handleAddNew}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-800 transition-colors"
            >
              <PlusIcon className="h-5 w-5 inline-block mr-1" />
              Add New
            </button>
          </div>
          
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="publication-list">
              {(provided: any) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="space-y-4"
                >
                  {publications.map((pub, index) => (
                    <Draggable key={pub.id} draggableId={pub.id!} index={index}>
                      {(provided: any) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className="border border-gray-200 dark:border-gray-700 rounded-lg p-5 bg-gray-50 dark:bg-gray-800 hover:shadow-md transition-shadow"
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-grow">
                              <div className="flex items-start">
                                {pub.coverImage ? (
                                  <div className="w-16 h-16 flex-shrink-0 mr-4 bg-gray-100 dark:bg-gray-700 rounded-md flex items-center justify-center overflow-hidden">
                                    <img 
                                      src={pub.coverImage} 
                                      alt={`Cover for ${pub.title}`} 
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                ) : (
                                  <div className="w-16 h-16 flex-shrink-0 mr-4 bg-gray-100 dark:bg-gray-700 rounded-md flex items-center justify-center">
                                    <DocumentIcon className="h-8 w-8 text-gray-400" />
                                  </div>
                                )}
                                <div className="w-full">
                                  <div className="flex flex-wrap justify-between items-start mb-1">
                                    <h3 className="font-semibold text-lg text-gray-900 dark:text-white mr-2">{pub.title}</h3>
                                    <div className="flex space-x-2">
                                      <button
                                        type="button"
                                        onClick={() => handleEdit(pub)}
                                        className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md"
                                      >
                                        Edit
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDelete(pub.id!)}
                                        className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md"
                                      >
                                        <TrashIcon className="h-5 w-5" />
                                      </button>
                                    </div>
                                  </div>
                                  <p className="text-gray-700 dark:text-gray-300 text-sm">
                                    {pub.authors.join(', ')}
                                  </p>
                                  <div className="flex flex-wrap items-center text-sm text-gray-500 dark:text-gray-400 mt-1 mb-2">
                                    {pub.conference && <span>{pub.conference}</span>}
                                    {pub.journal && <span>{pub.journal}</span>}
                                    {(pub.conference || pub.journal) && pub.year && <span className="mx-2">•</span>}
                                    {pub.year && <span>{pub.year}</span>}
                                    {((pub.conference || pub.journal) || pub.year) && pub.location && <span className="mx-2">•</span>}
                                    {pub.location && <span>{pub.location}</span>}
                                  </div>
                                  
                                  {(pub.abstract || pub.doi || pub.link || pub.keywords?.length) && (
                                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                                      {pub.abstract && (
                                        <div className="mb-2">
                                          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium mb-1">Abstract:</p>
                                          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{pub.abstract}</p>
                                        </div>
                                      )}
                                      
                                      {pub.keywords && pub.keywords.length > 0 && (
                                        <div className="mb-2">
                                          <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium mb-1">Keywords:</p>
                                          <div className="flex flex-wrap gap-1">
                                            {pub.keywords.map((keyword, idx) => (
                                              <span key={idx} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-xs rounded-full text-gray-600 dark:text-gray-300">
                                                {keyword}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {(pub.doi || pub.link) && (
                                        <div className="flex gap-3 mt-2">
                                          {pub.doi && (
                                            <a 
                                              href={`https://doi.org/${pub.doi}`} 
                                              target="_blank" 
                                              rel="noopener noreferrer" 
                                              className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                                            >
                                              DOI: {pub.doi}
                                            </a>
                                          )}
                                          {pub.link && (
                                            <a 
                                              href={pub.link} 
                                              target="_blank" 
                                              rel="noopener noreferrer" 
                                              className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                                            >
                                              View Publication
                                            </a>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      )}
      
      {/* No Publications Message */}
      {publications.length === 0 && !isAdding && !selectedPublication && (
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-md mb-8 text-center">
          <p className="text-gray-600 dark:text-gray-400 mb-4">No publications found. Add your first one now!</p>
          <button
            type="button"
            onClick={handleAddNew}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-800 transition-colors"
          >
            <PlusIcon className="h-5 w-5 inline-block mr-1" />
            Add Publication
          </button>
        </div>
      )}
      
      {/* Add/Edit Form - Simple version */}
      {(isAdding || selectedPublication) && (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {isAdding ? 'Add New Publication' : 'Edit Publication'}
            </h2>
            <button
              type="button"
              onClick={handleCancel}
              className="p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          
          <Formik
            initialValues={selectedPublication || initialValues}
            validationSchema={publicationSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ values, setFieldValue, isSubmitting }) => (
              <Form className="space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300" htmlFor="title">
                    Title*
                  </label>
                  <Field
                    id="title"
                    name="title"
                    type="text"
                    className="form-input w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    placeholder="Publication title"
                  />
                  <ErrorMessage name="title" component="p" className="text-red-500 text-xs mt-1" />
                </div>
                
                {/* Authors - Dynamic List */}
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Authors*
                  </label>
                  <FieldArray name="authors">
                    {({ push, remove }) => (
                      <div className="space-y-3">
                        {values.authors && values.authors.map((_, index) => (
                          <div key={index} className="flex items-start">
                            <Field
                              name={`authors.${index}`}
                              type="text"
                              className="form-input w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                              placeholder="Author name"
                            />
                            <button
                              type="button"
                              onClick={() => remove(index)}
                              className="ml-2 p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md"
                              disabled={values.authors && values.authors.length === 1}
                            >
                              <XMarkIcon className="h-5 w-5" />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => push('')}
                          className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 text-sm transition-colors"
                        >
                          <PlusIcon className="h-4 w-4 inline-block mr-1" />
                          Add Author
                        </button>
                      </div>
                    )}
                  </FieldArray>
                </div>
                
                {/* Basic fields in two columns */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300" htmlFor="year">
                      Year*
                    </label>
                    <Field
                      id="year"
                      name="year"
                      type="text"
                      className="form-input w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      placeholder="Publication year"
                    />
                    <ErrorMessage name="year" component="p" className="text-red-500 text-xs mt-1" />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300" htmlFor="location">
                      Location
                    </label>
                    <Field
                      id="location"
                      name="location"
                      type="text"
                      className="form-input w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      placeholder="e.g., New York, USA"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300" htmlFor="conference">
                      Conference
                    </label>
                    <Field
                      id="conference"
                      name="conference"
                      type="text"
                      className="form-input w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      placeholder="Conference name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300" htmlFor="journal">
                      Journal
                    </label>
                    <Field
                      id="journal"
                      name="journal"
                      type="text"
                      className="form-input w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      placeholder="Journal name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300" htmlFor="doi">
                      DOI
                    </label>
                    <Field
                      id="doi"
                      name="doi"
                      type="text"
                      className="form-input w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      placeholder="Digital Object Identifier"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300" htmlFor="link">
                      Link
                    </label>
                    <Field
                      id="link"
                      name="link"
                      type="url"
                      className="form-input w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      placeholder="URL to publication"
                    />
                    <ErrorMessage name="link" component="p" className="text-red-500 text-xs mt-1" />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300" htmlFor="abstract">
                      Abstract
                    </label>
                    <Field
                      as="textarea"
                      id="abstract"
                      name="abstract"
                      rows={4}
                      className="form-textarea w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      placeholder="Abstract of the publication"
                    />
                  </div>
                </div>
                
                {/* Keywords - Dynamic List */}
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Keywords
                  </label>
                  <FieldArray name="keywords">
                    {({ push, remove }) => (
                      <div className="space-y-3">
                        {values.keywords && values.keywords.map((_, index) => (
                          <div key={index} className="flex items-start">
                            <Field
                              name={`keywords.${index}`}
                              type="text"
                              className="form-input w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                              placeholder="Keyword"
                            />
                            <button
                              type="button"
                              onClick={() => remove(index)}
                              className="ml-2 p-1.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md"
                              disabled={values.keywords && values.keywords.length === 1}
                            >
                              <XMarkIcon className="h-5 w-5" />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => push('')}
                          className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 text-sm transition-colors"
                        >
                          <PlusIcon className="h-4 w-4 inline-block mr-1" />
                          Add Keyword
                        </button>
                      </div>
                    )}
                  </FieldArray>
                </div>
                
                {/* Featured checkbox */}
                <div className="flex items-center">
                  <Field
                    type="checkbox"
                    id="featured"
                    name="featured"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="featured" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                    Featured publication (will be highlighted)
                  </label>
                </div>
                
                {/* Form Buttons */}
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-800 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Saving...' : selectedPublication ? 'Update Publication' : 'Add Publication'}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      )}
    </div>
  );
};

export default PublicationsEditor; 